// LibP2P Client for Halloween Game Statistics Dashboard
// Connects to MQTT Ingestor via libp2p and queries game data

import { createLibp2p } from 'libp2p';
import { webSockets } from '@libp2p/websockets';
import { webTransport } from '@libp2p/webtransport';
import { webRTC } from '@libp2p/webrtc';
import { noise } from '@chainsafe/libp2p-noise';
import { yamux } from '@chainsafe/libp2p-yamux';
import { identify } from '@libp2p/identify';
import { gossipsub } from '@chainsafe/libp2p-gossipsub';
import { multiaddr } from '@multiformats/multiaddr';

const API_PROTOCOL = '/meshflare/halloween-api/1.0.0';
const BOOTSTRAP_ENDPOINT = 'https://bootstrap.meshpt.contact/bootstrap';
const GOSSIP_TOPIC = 'p3z-Halloween'; // Same topic as the game uses
const REFRESH_INTERVAL = 10000; // 10 seconds
const MOCK_MODE = false; // Set to true to use mock data for testing UI

// WASM instance for decoding protobufs
let wasmInstance = null;

let libp2pNode = null;
let ingestorPeer = null;
let isConnected = false;
let map = null;
let nodeMarkers = [];
let heatmapLayer = null;
let blastRadiusLayers = [];
let currentView = 'nodes';
let showBlastRadius = false;
let recentAttacks = []; // Store recent attacks for blast radius visualization
const MAX_RECENT_ATTACKS = 10; // Keep last 10 attacks

// Load WASM for protobuf decoding
async function loadWasm() {
    try {
        console.log('📦 Loading WASM decoder...');
        
        // Load wasm_exec.js (not wasm_exec_new.js - that's for the static site)
        const wasmExecScript = document.createElement('script');
        wasmExecScript.src = '/dist/wasm_exec.js';
        await new Promise((resolve, reject) => {
            wasmExecScript.onload = resolve;
            wasmExecScript.onerror = reject;
            document.head.appendChild(wasmExecScript);
        });
        
        console.log('✅ wasm_exec.js loaded');
        
        // Initialize Go runtime
        const go = new Go();
        
        // Load the WASM module with cache busting
        const wasmUrl = `/dist/main.wasm?v=${Date.now()}`;
        console.log('📥 Fetching WASM from:', wasmUrl);
        const response = await fetch(wasmUrl);
        const buffer = await response.arrayBuffer();
        const result = await WebAssembly.instantiate(buffer, go.importObject);
        wasmInstance = result.instance;
        
        // Run the WASM module - this is async and exports functions when ready
        // Don't await it as it runs forever, but wait for the function to be exported
        go.run(wasmInstance);
        
        console.log('✅ WASM decoder loaded and running');
        
        // Poll for the required functions to become available
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max wait
        while (attempts < maxAttempts) {
            const hasDecodeMeshPacket = typeof window.decodeMeshPacket === 'function';
            const hasDecodeHalloween = typeof window.decodeHalloweenMessage === 'function';
            
            // Log what we find every 20 attempts (every 2 seconds)
            if (attempts % 20 === 0 && attempts > 0) {
                console.log(`⏳ Waiting for WASM exports... (${attempts * 100}ms elapsed)`);
                console.log('   Available:', {
                    decodeMeshPacket: hasDecodeMeshPacket,
                    decodeHalloweenMessage: hasDecodeHalloween,
                    processFromRadio: typeof window.processFromRadio === 'function',
                    getAppVersion: typeof window.getAppVersion === 'function'
                });
            }
            
            if (hasDecodeMeshPacket && hasDecodeHalloween) {
                console.log('✅ Dashboard decoder functions available!');
                console.log('   - decodeMeshPacket:', typeof window.decodeMeshPacket);
                console.log('   - decodeHalloweenMessage:', typeof window.decodeHalloweenMessage);
                return true;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }
        
        console.warn('⚠️ decodeMeshPacket function not found after 10s');
        console.log('   Available functions:', {
            processFromRadio: typeof window.processFromRadio,
            getAppVersion: typeof window.getAppVersion,
            decodeMeshPacket: typeof window.decodeMeshPacket,
            decodeHalloweenMessage: typeof window.decodeHalloweenMessage
        });
        return false;
    } catch (error) {
        console.error('❌ Failed to load WASM:', error);
        return false;
    }
}

// Initialize on page load
window.addEventListener('DOMContentLoaded', async () => {
    console.log('🎃 Initializing Halloween Stats Dashboard...');
    
    // Initialize map
    initMap();
    
    // Load WASM decoder
    await loadWasm();
    
    // Connect to P2P network
    await initLibP2P();
    
    // Start periodic refresh
    setInterval(refreshData, REFRESH_INTERVAL);
    
    // Update activity timestamps every second
    setInterval(updateActivityTimestamps, 1000);
});

// Initialize Leaflet map
function initMap() {
    map = L.map('map').setView([39.5, -8.0], 7); // Center on Portugal
    
    // Dark theme tile layer
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
    }).addTo(map);
    
    console.log('🗺️ Map initialized');
}

// Initialize libp2p node
async function initLibP2P() {
    try {
        updateConnectionStatus('connecting', 'Connecting to P3Z Network...');
        
        // Fetch bootstrap multiaddrs
        console.log('📡 Fetching bootstrap info...');
        const bootstrapData = await fetchBootstrap();
        
        if (!bootstrapData || !bootstrapData.multiaddrs || bootstrapData.multiaddrs.length === 0) {
            throw new Error('No bootstrap multiaddrs available');
        }
        
        console.log('📍 Bootstrap multiaddrs:', bootstrapData.multiaddrs);
        
        // Create libp2p node
        console.log('🔧 Creating libp2p node...');
        
        // Detect mobile devices for transport selection
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const useWebRTC = isMobile;
        
        console.log('📱 Platform:', isMobile ? 'Mobile (using WebRTC)' : 'Desktop (using WebTransport)');
        
        libp2pNode = await createLibp2p({
            addresses: {
                listen: []
            },
            transports: useWebRTC ? [webRTC()] : [webTransport()],
            connectionEncryption: [noise()],
            streamMuxers: [yamux()],
            connectionManager: {
                minConnections: 1,
                maxConnections: 10,
                autoDialInterval: 1000,
                dialTimeout: 30000
            },
            services: {
                identify: identify(),
                pubsub: gossipsub({
                    emitSelf: false,
                    allowPublishToZeroTopicPeers: true
                })
            }
        });
        
        await libp2pNode.start();
        console.log('✅ LibP2P node started with Peer ID:', libp2pNode.peerId.toString());
        
        // Subscribe to gossip topic for real-time activity
        subscribeToGossip();
        
        // Connect to ingestor
        await connectToIngestor(bootstrapData.multiaddrs);
        
    } catch (error) {
        console.error('❌ Failed to initialize libp2p:', error);
        updateConnectionStatus('disconnected', `Connection failed: ${error.message}`);
        showError('Failed to connect to P2P network. Retrying in 10 seconds...');
        
        // Retry after 10 seconds
        setTimeout(initLibP2P, 10000);
    }
}

// Fetch bootstrap information
async function fetchBootstrap() {
    try {
        const response = await fetch(BOOTSTRAP_ENDPOINT, {
            cache: 'no-cache'
        });
        
        if (!response.ok) {
            throw new Error(`Bootstrap fetch failed: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('📥 Bootstrap data:', data);
        return data;
        
    } catch (error) {
        console.error('❌ Failed to fetch bootstrap:', error);
        throw error;
    }
}

// Connect to ingestor peer
async function connectToIngestor(multiaddrs) {
    // Detect platform to choose correct transport
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Select the appropriate multiaddr based on platform
    // Desktop: WebTransport (QUIC) - port 9090
    // Mobile: WebRTC Direct - port 9091
    const targetAddr = isMobile ? multiaddrs.webrtc : multiaddrs.webtransport;
    
    if (!targetAddr) {
        throw new Error(`No ${isMobile ? 'WebRTC' : 'WebTransport'} multiaddr available`);
    }
    
    try {
        console.log(`🔌 Connecting to ingestor via ${isMobile ? 'WebRTC' : 'WebTransport'}:`, targetAddr);
        const addr = multiaddr(targetAddr);
        
        const connection = await libp2pNode.dial(addr);
        ingestorPeer = connection.remotePeer;
        isConnected = true;
        
        console.log('✅ Connected to ingestor:', ingestorPeer.toString());
        updateConnectionStatus('connected', `Connected via ${isMobile ? 'WebRTC' : 'WebTransport'}`);
        
        // Load initial data
        await refreshData();
        
    } catch (error) {
        console.error('❌ Failed to connect to ingestor:', error.message);
        updateConnectionStatus('disconnected', 'Connection failed');
        throw error;
    }
}

// Query API via libp2p
async function queryAPI(request) {
    // Mock mode for testing UI without server
    if (MOCK_MODE) {
        await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network delay
        
        switch (request.action) {
            case 'scoreboard':
                return {
                    scoreboard: [
                        {
                            nodeId: 2886795292,
                            shortName: "GZ01",
                            longName: "GonZo's Node",
                            latitude: 38.7223,
                            longitude: -9.1393,
                            scaresSent: 45,
                            scaresReceived: 12,
                            zombieAttacksSent: 8,
                            zombieAttacksReceived: 3,
                            blackCatsSent: 15,
                            blackCatsReceived: 7,
                            ghostsSent: 20,
                            vampiresSent: 10,
                            witchesSent: 8,
                            skeletonsSent: 5,
                            zombiesSent: 2,
                            prizesReceived: 3,
                            packetCount: 127,
                            lastSeen: Math.floor(Date.now() / 1000) - 300,
                            lastRssi: -82,
                            lastSnr: 7.5,
                            totalScore: 98
                        },
                        {
                            nodeId: 305419896,
                            shortName: "DOM3",
                            longName: "Domingos Node",
                            latitude: 38.7500,
                            longitude: -9.1500,
                            scaresSent: 32,
                            scaresReceived: 15,
                            zombieAttacksSent: 5,
                            zombieAttacksReceived: 2,
                            blackCatsSent: 12,
                            blackCatsReceived: 8,
                            ghostsSent: 15,
                            vampiresSent: 8,
                            witchesSent: 6,
                            skeletonsSent: 3,
                            zombiesSent: 1,
                            prizesReceived: 2,
                            packetCount: 89,
                            lastSeen: Math.floor(Date.now() / 1000) - 120,
                            lastRssi: -75,
                            lastSnr: 9.2,
                            totalScore: 70
                        },
                        {
                            nodeId: 123456789,
                            shortName: "P3Z",
                            longName: "Test Node Lisboa",
                            latitude: 38.7300,
                            longitude: -9.1400,
                            scaresSent: 28,
                            scaresReceived: 10,
                            zombieAttacksSent: 4,
                            zombieAttacksReceived: 1,
                            blackCatsSent: 10,
                            blackCatsReceived: 5,
                            ghostsSent: 12,
                            vampiresSent: 6,
                            witchesSent: 4,
                            skeletonsSent: 2,
                            zombiesSent: 1,
                            prizesReceived: 1,
                            packetCount: 67,
                            lastSeen: Math.floor(Date.now() / 1000) - 600,
                            lastRssi: -88,
                            lastSnr: 6.1,
                            totalScore: 55
                        }
                    ],
                    sortBy: request.sortBy || 'score',
                    timestamp: Math.floor(Date.now() / 1000)
                };
                
            case 'global_stats':
                return {
                    totalNodes: 87,
                    activeNodes: 42,
                    totalPackets: 15847,
                    totalScares: 2347,
                    totalZombieAttacks: 542,
                    totalBlackCats: 1205,
                    totalGhosts: 1456,
                    totalVampires: 523,
                    totalWitches: 287,
                    totalSkeletons: 178,
                    totalZombies: 103,
                    totalPrizes: 234,
                    averageRssi: -85.3,
                    averageSnr: 6.8,
                    timestamp: Math.floor(Date.now() / 1000),
                    uptime: 86400
                };
            
            case 'recent_attacks':
                return {
                    attacks: [
                        {
                            messageId: 'attack-msg-001',
                            attackerNodeId: 2886795292,
                            targetNodeId: 305419896,
                            attackType: 'scare',
                            scareType: 'ghost',
                            timestamp: Math.floor(Date.now() / 1000) - 120
                        },
                        {
                            messageId: 'attack-msg-002',
                            attackerNodeId: 305419896,
                            targetNodeId: 123456789,
                            attackType: 'zombie_horde',
                            timestamp: Math.floor(Date.now() / 1000) - 180
                        },
                        {
                            messageId: 'attack-msg-003',
                            attackerNodeId: 123456789,
                            targetNodeId: 2886795292,
                            attackType: 'black_cat',
                            timestamp: Math.floor(Date.now() / 1000) - 240
                        }
                    ],
                    timestamp: Math.floor(Date.now() / 1000)
                };
            
            case 'blast_radius':
                // Mock blast radius data for the given message
                const messageIdNum = request.messageId.split('-')[2];
                const baseTime = Math.floor(Date.now() / 1000) - 300;
                
                return {
                    success: true,
                    data: {
                        messageId: request.messageId,
                        observations: [
                            {
                                id: 1,
                                messageId: request.messageId,
                                attackerNodeId: 2886795292,
                                targetNodeId: 305419896,
                                attackType: 'scare',
                                scareType: 'ghost',
                                observedVia: 'p2p',
                                observerPeerId: '12D3KooWP2R8ttLP1hPo',
                                observedAt: baseTime,
                                payloadSize: 128,
                                senderLocation: {
                                    latitude: 38.7223,
                                    longitude: -9.1393,
                                    altitude: 10
                                }
                            },
                            {
                                id: 2,
                                messageId: request.messageId,
                                attackerNodeId: 2886795292,
                                targetNodeId: 305419896,
                                attackType: 'scare',
                                scareType: 'ghost',
                                observedVia: 'mqtt',
                                gatewayId: '!abcd1234',
                                observedAt: baseTime + 2,
                                rssi: -89,
                                snr: 8.5,
                                hops: 2,
                                victimConfirmation: true,
                                victimNodeId: 305419896,
                                payloadSize: 128,
                                senderLocation: {
                                    latitude: 38.7223,
                                    longitude: -9.1393,
                                    altitude: 10
                                },
                                observerLocation: {
                                    latitude: 38.7500,
                                    longitude: -9.1500,
                                    altitude: 25
                                }
                            },
                            {
                                id: 3,
                                messageId: request.messageId,
                                attackerNodeId: 2886795292,
                                targetNodeId: 305419896,
                                attackType: 'scare',
                                scareType: 'ghost',
                                observedVia: 'mqtt',
                                gatewayId: '!efgh5678',
                                observedAt: baseTime + 4,
                                rssi: -95,
                                snr: 6.2,
                                hops: 3,
                                payloadSize: 128,
                                senderLocation: {
                                    latitude: 38.7223,
                                    longitude: -9.1393,
                                    altitude: 10
                                },
                                observerLocation: {
                                    latitude: 38.7300,
                                    longitude: -9.1400,
                                    altitude: 15
                                }
                            }
                        ],
                        totalObservations: 3,
                        mqttObservations: 2,
                        p2pObservations: 1,
                        uniqueObservers: 3,
                        propagationTimeMs: 4000,
                        firstObservedAt: baseTime,
                        lastObservedAt: baseTime + 4,
                        timestamp: Math.floor(Date.now() / 1000)
                    }
                };
                
            default:
                throw new Error('Unknown action in mock mode');
        }
    }
    
    // Real libp2p API mode
    if (!libp2pNode || !ingestorPeer || !isConnected) {
        throw new Error('Not connected to P2P network');
    }
    
    try {
        // Open stream to API protocol
        const stream = await libp2pNode.dialProtocol(ingestorPeer, API_PROTOCOL);
        
        // Send request
        const encoder = new TextEncoder();
        const requestJSON = JSON.stringify(request);
        await stream.sink([encoder.encode(requestJSON)]);
        
        // Read response
        const decoder = new TextDecoder();
        let responseData = '';
        
        for await (const chunk of stream.source) {
            responseData += decoder.decode(chunk.subarray());
        }
        
        // Close stream
        await stream.close();
        
        // Parse response
        const response = JSON.parse(responseData);
        
        if (!response.success) {
            throw new Error(response.error || 'API request failed');
        }
        
        return response.data;
        
    } catch (error) {
        console.error('❌ API query failed:', error);
        
        // Mark as disconnected if connection error
        if (error.message.includes('connection') || error.message.includes('stream')) {
            isConnected = false;
            updateConnectionStatus('disconnected', 'Connection lost');
        }
        
        throw error;
    }
}

// Refresh all data
async function refreshData() {
    if (!isConnected) {
        console.log('⏸️ Skipping refresh - not connected');
        return;
    }
    
    try {
        // Query global stats
        const globalStats = await queryAPI({ action: 'global_stats' });
        updateGlobalStats(globalStats);
        
        // Query leaderboard (sortBy: 'score' uses the ingestor's calculated score)
        const leaderboard = await queryAPI({ 
            action: 'scoreboard',
            limit: 20,
            sortBy: 'score'
        });
        updateLeaderboard(leaderboard);
        
        // Query all active nodes for map
        const allNodes = await queryAPI({
            action: 'scoreboard',
            limit: 100,
            sortBy: 'score'
        });
        updateMap(allNodes);
        
        // Load blast radius for last 3 attacks if enabled
        if (showBlastRadius) {
            await updateBlastRadius();
        }
        
    } catch (error) {
        console.error('❌ Failed to refresh data:', error);
        showError(`Failed to refresh: ${error.message}`);
    }
}

// Update global statistics
function updateGlobalStats(stats) {
    document.getElementById('totalNodes').textContent = stats.totalNodes || 0;
    document.getElementById('activeNodes').textContent = stats.activeNodes || 0;
    
    const totalAttacks = (stats.totalScares || 0) + 
                        (stats.totalZombieAttacks || 0) + 
                        (stats.totalBlackCats || 0);
    document.getElementById('totalAttacks').textContent = totalAttacks.toLocaleString();
    document.getElementById('totalPrizes').textContent = stats.totalPrizes || 0;
}

// Update leaderboard
function updateLeaderboard(data) {
    const container = document.getElementById('leaderboardContainer');
    const scoreboard = data.scoreboard || [];
    
    if (scoreboard.length === 0) {
        container.innerHTML = '<p class="loading">No players yet...</p>';
        return;
    }
    
    const html = scoreboard.map((player, index) => {
        const rank = index + 1;
        let rankClass = '';
        if (rank === 1) rankClass = 'gold';
        else if (rank === 2) rankClass = 'silver';
        else if (rank === 3) rankClass = 'bronze';
        
        // Use the totalScore from database (RSSI-based scoring formula)
        const score = player.totalScore || 0;
        
        return `
            <li class="leaderboard-item" onclick="focusNode(${player.nodeId})">
                <div class="rank ${rankClass}">${rank}</div>
                <div class="player-info">
                    <div class="player-name">${escapeHtml(player.shortName || player.longName || 'Unknown')}</div>
                    <div class="player-stats">
                        👻 ${player.scaresSent || 0} | 🧟 ${player.zombieAttacksSent || 0} | 🐱 ${player.blackCatsSent || 0}
                    </div>
                </div>
                <div class="score">${score}</div>
            </li>
        `;
    }).join('');
    
    container.innerHTML = `<ul class="leaderboard">${html}</ul>`;
}

// Update blast radius visualization
async function updateBlastRadius() {
    try {
        console.log('💥 Updating blast radius visualization...');
        
        // Clear existing blast radius layers
        blastRadiusLayers.forEach(layer => map.removeLayer(layer));
        blastRadiusLayers = [];
        
        // Use locally stored recent attacks instead of querying API
        if (!recentAttacks || recentAttacks.length === 0) {
            console.log('No recent attacks to display');
            return;
        }
        
        // Get up to 3 most recent attacks
        const attacksToShow = recentAttacks.slice(0, 3);
        console.log(`📡 Displaying ${attacksToShow.length} recent attacks`);
        
        // Color scheme for different attacks
        const colors = ['#ff0000', '#ff6600', '#ffaa00'];
        
        // Process each attack - simplified version without API calls
        for (let i = 0; i < attacksToShow.length; i++) {
            const attack = attacksToShow[i];
            const color = colors[i];
            
            try {
                // Check if attack has location data
                if (!attack.latitude || !attack.longitude) {
                    console.warn(`No location data for attack from ${attack.senderName || attack.senderId}`);
                    continue;
                }
                
                // Add sender marker
                const senderMarker = L.circleMarker([attack.latitude, attack.longitude], {
                    radius: 10,
                    fillColor: color,
                    color: '#fff',
                    weight: 3,
                    opacity: 1,
                    fillOpacity: 0.8
                }).bindPopup(`
                    <div class="blast-popup">
                        <h4>💥 Attack</h4>
                        <p><strong>From:</strong> ${escapeHtml(attack.senderName || 'Unknown')}</p>
                        <p><strong>Type:</strong> ${attack.type || 'Unknown'}</p>
                        <p><strong>Time:</strong> ${formatTimestamp(attack.timestamp)}</p>
                    </div>
                `);
                senderMarker.addTo(map);
                blastRadiusLayers.push(senderMarker);
                
                // Draw a simple range circle (estimated ~5km for LoRa)
                const rangeCircle = L.circle([attack.latitude, attack.longitude], {
                    radius: 5000, // 5km in meters
                    color: color,
                    weight: 2,
                    fillOpacity: 0.05,
                    opacity: 0.5,
                    dashArray: '10, 10'
                }).bindPopup(`
                    <div class="blast-popup">
                        <h4>💥 Estimated Range</h4>
                        <p><strong>Range:</strong> ~5 km</p>
                        <p><strong>From:</strong> ${escapeHtml(attack.senderName || 'Unknown')}</p>
                    </div>
                `);
                rangeCircle.addTo(map);
                blastRadiusLayers.push(rangeCircle);
                
            } catch (error) {
                console.error(`Failed to display attack from ${attack.senderName}:`, error);
            }
        }
        
        console.log(`✅ Added ${blastRadiusLayers.length} blast radius layers`);
        
    } catch (error) {
        console.error('❌ Failed to update blast radius:', error);
    }
}

// Calculate distance between two GPS coordinates using Haversine formula
function calculateDistance(loc1, loc2) {
    const R = 6371; // Earth radius in km
    const dLat = (loc2.latitude - loc1.latitude) * Math.PI / 180;
    const dLon = (loc2.longitude - loc1.longitude) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(loc1.latitude * Math.PI / 180) * 
        Math.cos(loc2.latitude * Math.PI / 180) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Get color based on RSSI value
function getRSSIColor(rssi) {
    if (rssi > -80) return '#00ff00';   // Strong signal - green
    if (rssi > -100) return '#ffff00';  // Medium signal - yellow
    return '#ff0000';                   // Weak signal - red
}

// Enrich recent attacks with location data from scoreboard
function enrichAttacksWithLocation(scoreboard) {
    recentAttacks.forEach(attack => {
        // Skip if already has location
        if (attack.latitude && attack.longitude) return;
        
        // Find matching node in scoreboard
        const node = scoreboard.find(n => {
            const nodeIdHex = n.nodeId?.toString(16).toUpperCase().padStart(8, '0');
            return nodeIdHex === attack.senderId;
        });
        
        if (node && node.latitude && node.longitude) {
            attack.latitude = node.latitude;
            attack.longitude = node.longitude;
            console.log(`📍 Enriched attack from ${attack.senderName} with location`);
        }
    });
}

// Update map markers
function updateMap(data) {
    const scoreboard = data.scoreboard || [];
    
    console.log('🗺️ updateMap called with', scoreboard.length, 'nodes');
    
    // Debug: Show all nodes to see their GPS data
    if (scoreboard.length > 0) {
        console.log('📊 All nodes:');
        scoreboard.forEach(n => {
            console.log(`  - ${n.shortName || n.longName}: lat=${n.latitude}, lng=${n.longitude}, nodeId=${n.nodeId}`);
        });
        console.log('📋 Full first node:', scoreboard[0]);
        console.log('📋 All field names:', Object.keys(scoreboard[0]));
    }
    
    // Clear existing markers
    nodeMarkers.forEach(marker => map.removeLayer(marker));
    nodeMarkers = [];
    
    // Filter nodes with valid GPS coordinates
    const nodesWithGPS = scoreboard.filter(node => 
        node.latitude && 
        node.longitude && 
        node.latitude !== 0 && 
        node.longitude !== 0
    );
    
    console.log('📍 Found', nodesWithGPS.length, 'nodes with GPS coordinates');
    
    // Enrich recent attacks with location data from scoreboard
    enrichAttacksWithLocation(scoreboard);
    
    if (currentView === 'nodes') {
        // Add markers for each node
        nodesWithGPS.forEach(node => {
            console.log('🏷️ Creating marker for:', node.shortName, 'at', [node.latitude, node.longitude]);
            
            // Use the totalScore from database (RSSI-based scoring formula)
            const score = node.totalScore || 0;
            
            // Determine marker color based on score
            let markerColor = '#ff6b00';
            if (score > 100) markerColor = '#ff0000';
            else if (score > 40) markerColor = '#ff4500';
            else if (score < 10) markerColor = '#ffa500';
            
            const marker = L.circleMarker([node.latitude, node.longitude], {
                radius: 8 + Math.min(score / 10, 15),
                fillColor: markerColor,
                color: '#fff',
                weight: 2,
                opacity: 1,
                fillOpacity: 0.7
            });
            
            // Create popup
            const popupContent = `
                <div class="node-popup">
                    <h3>🎃 ${escapeHtml(node.shortName || node.longName || 'Unknown')}</h3>
                    <p><strong>Node ID:</strong> ${node.nodeId}</p>
                    <p><strong>Total Score:</strong> <span class="stat">${score}</span></p>
                    <p>👻 Scares: <span class="stat">${node.scaresSent || 0}</span></p>
                    <p>🧟 Zombies: <span class="stat">${node.zombieAttacksSent || 0}</span></p>
                    <p>🐱 Black Cats: <span class="stat">${node.blackCatsSent || 0}</span></p>
                    <p>📦 Packets: <span class="stat">${node.packetCount || 0}</span></p>
                    <p>📡 Last Seen: <span class="stat">${formatTimestamp(node.lastSeen)}</span></p>
                    ${node.lastRssi ? `<p>📶 RSSI: <span class="stat">${node.lastRssi} dBm</span></p>` : ''}
                    ${node.lastSnr ? `<p>📊 SNR: <span class="stat">${node.lastSnr.toFixed(1)} dB</span></p>` : ''}
                </div>
            `;
            
            marker.bindPopup(popupContent);
            marker.addTo(map);
            nodeMarkers.push(marker);
            
            // Store node data for later reference
            marker.nodeData = node;
        });
        
        console.log('✅ Added', nodeMarkers.length, 'markers to map');
    }
    
    // Update heatmap data
    if (heatmapLayer) {
        map.removeLayer(heatmapLayer);
    }
    
    const heatmapData = nodesWithGPS.map(node => {
        // Use the totalScore from database (RSSI-based scoring formula)
        const score = node.totalScore || 0;
        return [
            node.latitude,
            node.longitude,
            Math.min(score / 20, 1) // Normalize intensity (0-20+ score -> 0-1)
        ];
    });
    
    heatmapLayer = L.heatLayer(heatmapData, {
        radius: 25,
        blur: 35,
        maxZoom: 13,
        max: 1.0,
        gradient: {
            0.0: '#0000ff',
            0.2: '#00ffff',
            0.4: '#00ff00',
            0.6: '#ffff00',
            0.8: '#ff6b00',
            1.0: '#ff0000'
        }
    });
    
    if (currentView === 'heatmap') {
        heatmapLayer.addTo(map);
    }
    
    // Auto-fit bounds if nodes exist
    if (nodesWithGPS.length > 0) {
        const bounds = L.latLngBounds(nodesWithGPS.map(n => [n.latitude, n.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
}

// Focus on a specific node on the map
window.focusNode = function(nodeId) {
    console.log('🎯 focusNode called with nodeId:', nodeId);
    console.log('📍 Total markers:', nodeMarkers.length);
    console.log('🗺️ Current view:', currentView);
    
    // Switch to nodes view if not already
    if (currentView !== 'nodes') {
        console.log('🔄 Switching to nodes view...');
        showNodes();
    }
    
    const marker = nodeMarkers.find(m => m.nodeData && m.nodeData.nodeId === nodeId);
    console.log('🔍 Found marker:', !!marker);
    
    if (marker && marker.nodeData) {
        console.log('📌 Focusing on:', marker.nodeData.shortName, 'at', [marker.nodeData.latitude, marker.nodeData.longitude]);
        map.setView([marker.nodeData.latitude, marker.nodeData.longitude], 12);
        marker.openPopup();
    } else {
        console.warn('⚠️ No marker found for nodeId:', nodeId);
        console.log('Available node IDs:', nodeMarkers.map(m => m.nodeData?.nodeId));
    }
};

// Show nodes view
window.showNodes = function() {
    currentView = 'nodes';
    document.getElementById('btnNodes').classList.add('active');
    document.getElementById('btnHeatmap').classList.remove('active');
    
    if (heatmapLayer) {
        map.removeLayer(heatmapLayer);
    }
    
    nodeMarkers.forEach(marker => marker.addTo(map));
};

// Show heatmap view
window.showHeatmap = function() {
    currentView = 'heatmap';
    document.getElementById('btnHeatmap').classList.add('active');
    document.getElementById('btnNodes').classList.remove('active');
    
    nodeMarkers.forEach(marker => map.removeLayer(marker));
    
    if (heatmapLayer) {
        heatmapLayer.addTo(map);
    }
};

// Manual refresh
window.refreshData = async function() {
    document.getElementById('btnRefresh').style.opacity = '0.5';
    await refreshData();
    document.getElementById('btnRefresh').style.opacity = '1';
};

// Toggle blast radius visualization
window.toggleBlastRadius = async function() {
    showBlastRadius = !showBlastRadius;
    const btn = document.getElementById('btnBlastRadius');
    
    if (showBlastRadius) {
        btn.classList.add('active');
        btn.style.opacity = '0.5';
        await updateBlastRadius();
        btn.style.opacity = '1';
    } else {
        btn.classList.remove('active');
        // Clear blast radius layers
        blastRadiusLayers.forEach(layer => map.removeLayer(layer));
        blastRadiusLayers = [];
    }
};

// Update connection status UI
function updateConnectionStatus(status, message) {
    const statusBadge = document.getElementById('connectionStatus');
    const statusText = document.getElementById('statusText');
    
    statusBadge.className = `status-badge ${status}`;
    statusText.textContent = message;
    
    console.log(`📡 Connection status: ${status} - ${message}`);
}

// Show error message
function showError(message) {
    const container = document.getElementById('leaderboardContainer');
    const errorHTML = `
        <div class="error-message">
            <strong>⚠️ Error</strong><br>
            ${escapeHtml(message)}
        </div>
    `;
    container.innerHTML = errorHTML + container.innerHTML;
    
    // Remove error after 10 seconds
    setTimeout(() => {
        const errorDiv = container.querySelector('.error-message');
        if (errorDiv) errorDiv.remove();
    }, 10000);
}

// Subscribe to GossipSub for real-time activity feed
function subscribeToGossip() {
    if (!libp2pNode) return;
    
    try {
        const pubsub = libp2pNode.services.pubsub;
        
        // Subscribe to the Halloween topic
        pubsub.subscribe(GOSSIP_TOPIC);
        console.log('📡 Subscribed to gossip topic:', GOSSIP_TOPIC);
        
        // Listen for messages
        pubsub.addEventListener('message', (evt) => {
            const { topic, data, from } = evt.detail;
            
            console.log('📨 PubSub message received:', { topic, size: data?.length, from: from?.toString() });
            
            if (topic === GOSSIP_TOPIC) {
                try {
                    handleGossipMessage(data, from);
                } catch (error) {
                    console.error('Error handling gossip message:', error);
                }
            }
        });
        
    } catch (error) {
        console.error('Failed to subscribe to gossip:', error);
    }
}

// Handle incoming gossip message
function handleGossipMessage(data, from) {
    console.log('🔍 handleGossipMessage called:', { 
        dataLength: data?.length, 
        from: from?.toString(),
        wasmLoaded: !!wasmInstance,
        decodeFn: typeof window.decodeMeshPacket
    });
    
    // Note: WASM decoder is optional - we have a manual fallback
    // The game's WASM (main.wasm) doesn't export simple decode functions
    // so we rely on the manual protobuf parser below
    
    try {
        // Ensure data is a Uint8Array (WASM expects this, not a regular Array)
        const dataUint8 = data instanceof Uint8Array ? data : new Uint8Array(data);
        
        // Call WASM decode function
        if (typeof window.decodeMeshPacket === 'function') {
            const packet = window.decodeMeshPacket(dataUint8);
            
            console.log('📦 Decoded packet:', { 
                hasPacket: !!packet, 
                hasDecoded: !!packet?.decoded,
                portnum: packet?.decoded?.portnum,
                portnumType: typeof packet?.decoded?.portnum,
                payloadLength: packet?.decoded?.payload?.length
            });
            
            if (!packet) {
                // Skip messages that can't be decoded
                console.log('⏭️ Skipping undecoded packet');
                return;
            }
            
            if (!packet.decoded) {
                // Skip messages with no decoded data
                console.log('⏭️ Skipping packet with no decoded data');
                return;
            }
            
            // Check if it's a Halloween game packet (portnum 300)
            // Note: portnum might be a string or number, so use == instead of ===
            if (packet.decoded.portnum == 300) {
                console.log('🎃 Processing Halloween packet!');
                processHalloweenPacket(packet, from);
            } else {
                // Skip non-Halloween packets
                console.log('⏭️ Skipping non-Halloween packet, portnum:', packet.decoded.portnum);
            }
        } else {
            // Skip messages when WASM decoder is not available
            // We can't decode them properly without the decoder
            console.log('⏭️ Skipping message - WASM decoder not available');
        }
        
    } catch (error) {
        console.error('❌ Error processing gossip message:', error);
        console.error('Error stack:', error.stack);
    }
}

// Manual Halloween protobuf decoder (fallback when WASM not available)
// Simplified decoder for basic message types - just enough for activity feed
function decodeHalloweenManually(payload) {
    try {
        // Very basic protobuf parsing - just extract type and sender info
        // This is a simplified version that handles the common message structure
        
        if (payload.length < 2) return null;
        
        // Protobuf wire format: first byte contains field number and wire type
        // Based on halloween.proto:
        // Field 1 (message_id): string  
        // Field 2 (timestamp): uint32
        // Field 3 (type): HalloweenMessageType (enum/varint)
        // Field 4 (sender_node_id): uint32
        // Field 5 (sender_name): string
        // Field 10+ (oneof payload fields)
        
        let offset = 0;
        const msg = {
            type: 0,
            messageId: '',
            senderNodeId: 0,
            senderName: ''
        };
        
        // Simple protobuf parser - read until we find the key fields
        while (offset < payload.length) {
            if (offset >= payload.length) break;
            
            const tag = payload[offset++];
            const fieldNum = tag >> 3;
            const wireType = tag & 0x07;
            
            if (fieldNum === 3 && wireType === 0) { // type (varint)
                msg.type = payload[offset++];
            } else if (fieldNum === 4 && wireType === 0) { // senderNodeId (varint/uint32)
                // Read varint
                let value = 0;
                let shift = 0;
                while (offset < payload.length) {
                    const byte = payload[offset++];
                    value |= (byte & 0x7F) << shift;
                    if ((byte & 0x80) === 0) break;
                    shift += 7;
                }
                msg.senderNodeId = value;
            } else if (fieldNum === 5 && wireType === 2) { // senderName (string)
                const length = payload[offset++];
                if (offset + length <= payload.length) {
                    msg.senderName = new TextDecoder().decode(payload.slice(offset, offset + length));
                    offset += length;
                }
            } else {
                // Skip unknown fields
                if (wireType === 0) { // Varint
                    while (offset < payload.length && (payload[offset] & 0x80)) offset++;
                    offset++;
                } else if (wireType === 2) { // Length-delimited
                    const length = payload[offset++];
                    offset += length;
                } else {
                    offset++; // Skip
                }
            }
        }
        
        return msg;
    } catch (error) {
        console.error('❌ Manual decode failed:', error);
        return null;
    }
}

// Store a recent attack for blast radius visualization
function storeRecentAttack(attack) {
    try {
        // Add to beginning of array
        recentAttacks.unshift(attack);
        
        // Keep only the most recent attacks
        if (recentAttacks.length > MAX_RECENT_ATTACKS) {
            recentAttacks = recentAttacks.slice(0, MAX_RECENT_ATTACKS);
        }
        
        console.log(`💾 Stored attack, total: ${recentAttacks.length}`);
        
        // If blast radius is currently shown, update it
        if (showBlastRadius) {
            updateBlastRadius();
        }
    } catch (error) {
        console.error('❌ Failed to store attack:', error);
    }
}

// Process Halloween game packet
function processHalloweenPacket(packet, from) {
    try {
        const payload = packet.decoded.payload;
        console.log('🎃 processHalloweenPacket:', { 
            hasPayload: !!payload, 
            payloadLength: payload?.length,
            payloadType: payload?.constructor?.name 
        });
        
        if (!payload || payload.length === 0) {
            console.warn('⚠️ No payload in Halloween packet');
            return;
        }
        
        // Decode the Halloween protobuf message
        // Ensure payload is a Uint8Array (WASM expects this)
        const payloadUint8 = payload instanceof Uint8Array ? payload : new Uint8Array(payload);
        
        let halloweenMsg;
        
        // Try WASM decoder first if available
        if (typeof window.decodeHalloweenMessage === 'function') {
            console.log('🔧 Using WASM Halloween decoder');
            halloweenMsg = window.decodeHalloweenMessage(payloadUint8);
        } else {
            console.log('🔧 Using manual Halloween decoder (fallback)');
            // Fallback: decode manually (basic structure extraction)
            // This is a simplified decoder for the activity feed
            halloweenMsg = decodeHalloweenManually(payloadUint8);
        }
        
        console.log('🎃 Halloween message decoded:', { 
            hasMsg: !!halloweenMsg,
            msgType: halloweenMsg?.type,
            senderName: halloweenMsg?.senderName,
            senderNodeId: halloweenMsg?.senderNodeId
        });
        
        if (!halloweenMsg) {
            console.warn('⚠️ Failed to decode Halloween message');
            return;
        }
        
        // Extract node info and packet metadata
        const fromNode = packet.from || halloweenMsg.senderNodeId || 0;
        const toNode = packet.to || 0;
        const rssi = packet.rxRssi || 0;
        const snr = packet.rxSnr || 0;
        const hopLimit = packet.hopLimit || 0;
        const hopStart = packet.hopStart || 0;
        const hops = hopStart > 0 ? (hopStart - hopLimit) : 0;
        const viaMqtt = packet.viaMqtt || false;
        const channel = packet.channel || 0;
        
        let activity = {
            timestamp: Date.now(),
            fromNode: fromNode.toString(16).toUpperCase().padStart(8, '0'),
            toNode: toNode.toString(16).toUpperCase().padStart(8, '0'),
            senderName: halloweenMsg.senderName || null,
            rssi: rssi,
            snr: snr.toFixed(1),
            hops: hops,
            viaMqtt: viaMqtt,
            channel: channel,
            messageId: halloweenMsg.messageId || packet.id || 0
        };
        
        // Decode based on message type
        console.log('🔍 Switching on message type:', halloweenMsg.type);
        
        switch (halloweenMsg.type) {
            case 2: // HALLOWEEN_MESSAGE_TYPE_SCARE
                console.log('👻 Processing SCARE message');
                if (halloweenMsg.scare) {
                    activity.type = halloweenMsg.scare.scareType || 'scare';
                    activity.attacker = activity.senderName || `0x${activity.fromNode}`;
                    activity.victim = halloweenMsg.scare.targetNodeId 
                        ? `0x${halloweenMsg.scare.targetNodeId.toString(16).toUpperCase().padStart(8, '0')}`
                        : `0x${activity.toNode}`;
                    activity.message = halloweenMsg.scare.message;
                    activity.displayType = `${ACTIVITY_ICONS[activity.type] || '😱'} ${activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}`;
                } else {
                    // Fallback when manual decoder doesn't extract nested fields
                    activity.type = 'scare';
                    activity.attacker = activity.senderName || `0x${activity.fromNode}`;
                    activity.victim = `0x${activity.toNode}`;
                    activity.displayType = '😱 Scare';
                }
                break;
                
            case 3: // HALLOWEEN_MESSAGE_TYPE_ZOMBIE_HORDE
                console.log('🧟 Processing ZOMBIE_HORDE message');
                if (halloweenMsg.zombieHorde) {
                    activity.type = 'zombie_attack';
                    activity.attacker = activity.senderName || `0x${activity.fromNode}`;
                    activity.victim = halloweenMsg.zombieHorde.targetNodeId
                        ? `0x${halloweenMsg.zombieHorde.targetNodeId.toString(16).toUpperCase().padStart(8, '0')}`
                        : `0x${activity.toNode}`;
                    activity.zombieCount = halloweenMsg.zombieHorde.zombieCount;
                    activity.message = halloweenMsg.zombieHorde.message;
                    activity.displayType = `🧟 Zombie Horde (${activity.zombieCount})`;
                } else {
                    // Fallback
                    activity.type = 'zombie_attack';
                    activity.attacker = activity.senderName || `0x${activity.fromNode}`;
                    activity.victim = `0x${activity.toNode}`;
                    activity.displayType = '🧟 Zombie Horde';
                }
                break;
                
            case 4: // HALLOWEEN_MESSAGE_TYPE_BLACK_CAT
                console.log('🐈‍⬛ Processing BLACK_CAT message');
                if (halloweenMsg.blackCat) {
                    activity.type = 'black_cat';
                    activity.player = activity.senderName || `0x${activity.fromNode}`;
                    activity.direction = halloweenMsg.blackCat.direction;
                    activity.effect = halloweenMsg.blackCat.effect;
                    activity.displayType = '🐈‍⬛ Black Cat';
                } else {
                    // Fallback
                    activity.type = 'black_cat';
                    activity.player = activity.senderName || `0x${activity.fromNode}`;
                    activity.displayType = '🐈‍⬛ Black Cat';
                }
                break;
                
            case 1: // HALLOWEEN_MESSAGE_TYPE_CANDY_DROP
                console.log('🍬 Processing CANDY_DROP message');
                if (halloweenMsg.candyDrop) {
                    activity.type = 'candy';
                    activity.player = activity.senderName || `0x${activity.fromNode}`;
                    activity.candyType = halloweenMsg.candyDrop.candyType;
                    activity.candyEmoji = halloweenMsg.candyDrop.emoji;
                    activity.points = halloweenMsg.candyDrop.points;
                    activity.displayType = `${halloweenMsg.candyDrop.emoji} Candy Drop`;
                } else {
                    // Fallback
                    activity.type = 'candy';
                    activity.player = activity.senderName || `0x${activity.fromNode}`;
                    activity.displayType = '🍬 Candy Drop';
                }
                break;
                
            case 5: // HALLOWEEN_MESSAGE_TYPE_PRIZE
                console.log('🎁 Processing PRIZE message');
                if (halloweenMsg.prizePacket) {
                    activity.type = 'prize';
                    activity.player = activity.senderName || `0x${activity.fromNode}`;
                    activity.prizeType = halloweenMsg.prizePacket.prizeType;
                    activity.prizeValue = halloweenMsg.prizePacket.prizeValue;
                    activity.prizeMessage = halloweenMsg.prizePacket.message;
                    activity.prizeEmoji = halloweenMsg.prizePacket.emoji;
                    activity.displayType = `${halloweenMsg.prizePacket.emoji} Prize`;
                } else {
                    // Fallback
                    activity.type = 'prize';
                    activity.player = activity.senderName || `0x${activity.fromNode}`;
                    activity.displayType = '🎁 Prize';
                }
                break;
                
            case 6: // HALLOWEEN_MESSAGE_TYPE_VICTIM_CONFIRMATION
                console.log('✅ Processing VICTIM_CONFIRMATION message');
                if (halloweenMsg.victimConfirmation) {
                    activity.type = 'victim_confirmation';
                    activity.victim = activity.senderName || `0x${activity.fromNode}`;
                    activity.attacker = halloweenMsg.victimConfirmation.attackerNodeId
                        ? `0x${halloweenMsg.victimConfirmation.attackerNodeId.toString(16).toUpperCase().padStart(8, '0')}`
                        : 'Unknown';
                    activity.attackMessageId = halloweenMsg.victimConfirmation.attackMessageId;
                    activity.message = halloweenMsg.victimConfirmation.message;
                    activity.receivedAt = halloweenMsg.victimConfirmation.receivedAt;
                    activity.displayType = '✅ Victim Confirmed';
                } else {
                    // Fallback
                    activity.type = 'victim_confirmation';
                    activity.victim = activity.senderName || `0x${activity.fromNode}`;
                    activity.displayType = '✅ Victim Confirmed';
                }
                break;
                
            case 7: // HALLOWEEN_MESSAGE_TYPE_ATTACK
                console.log('🎯 Processing ATTACK message');
                if (halloweenMsg.attack) {
                    activity.type = halloweenMsg.attack.attackType || 'attack';
                    activity.attacker = activity.senderName || `0x${activity.fromNode}`;
                    activity.victim = halloweenMsg.attack.targetNodeId
                        ? `0x${halloweenMsg.attack.targetNodeId.toString(16).toUpperCase().padStart(8, '0')}`
                        : `0x${activity.toNode}`;
                    activity.message = halloweenMsg.attack.message;
                    activity.strength = halloweenMsg.attack.strength;
                    activity.cost = halloweenMsg.attack.cost;
                    
                    // Determine display based on attack type
                    if (activity.type === 'zombie_horde') {
                        activity.displayType = `🧟 Zombie Horde (${activity.strength})`;
                    } else {
                        const icon = ACTIVITY_ICONS[activity.type] || '⚔️';
                        activity.displayType = `${icon} ${activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}`;
                    }
                } else {
                    // Fallback
                    activity.type = 'attack';
                    activity.attacker = activity.senderName || `0x${activity.fromNode}`;
                    activity.victim = `0x${activity.toNode}`;
                    activity.displayType = '⚔️ Attack';
                }
                break;
                
            default:
                console.warn('⚠️ Unknown message type:', halloweenMsg.type);
                return;
        }
        
        // Store attacks for blast radius visualization
        if (activity.type === 'scare' || activity.type === 'zombie_attack' || activity.type === 'attack') {
            // Try to get location from the API for this node
            // For now, we'll add it without location and fetch location later if needed
            storeRecentAttack({
                timestamp: activity.timestamp,
                type: activity.type,
                senderId: activity.fromNode,
                senderName: activity.senderName,
                messageId: activity.messageId,
                // Location will be populated from node data if available
                latitude: null,
                longitude: null
            });
        }
        
        console.log('📤 Calling addActivityCard with:', activity);
        addActivityCard(activity);
        
    } catch (error) {
        console.error('Error processing Halloween packet:', error);
    }
}

// Activity card icons and types
const ACTIVITY_ICONS = {
    zombie_attack: '🧟',
    black_cat: '🐈‍⬛',
    ghost: '👻',
    vampire: '🧛',
    witch: '🧙',
    skeleton: '💀',
    zombie: '🧟‍♂️',
    prize: '🎁',
    scare: '😱',
    message: '📨',
    victim_confirmation: '✅',
    attack: '⚔️',
    zombie_horde: '🧟'
};

// Add activity card to the feed
function addActivityCard(activity) {
    const container = document.getElementById('activityContainer');
    if (!container) return;
    
    // Remove the "waiting" message if it exists
    if (container.querySelector('div[style*="text-align: center"]')) {
        container.innerHTML = '';
    }
    
    const card = document.createElement('div');
    card.className = `activity-card ${activity.type}`;
    
    const icon = ACTIVITY_ICONS[activity.type] || '📨';
    const time = formatActivityTime(activity.timestamp);
    
    let detailsHtml = '';
    let metadataHtml = '';
    
    // Build details based on activity type
    if (activity.type === 'victim_confirmation') {
        // Special handling for victim confirmations
        const victimDisplay = activity.senderName 
            ? `<strong>${escapeHtml(activity.senderName)}</strong> <span style="color: #64748b; font-size: 0.85em;">(${escapeHtml(activity.victim)})</span>`
            : `<span class="activity-player">${escapeHtml(activity.victim)}</span>`;
        
        detailsHtml = `
            <div class="activity-action">
                ${victimDisplay} confirmed attack from
                <span class="activity-player" style="color: #ef4444; margin-left: 0.5rem;">${escapeHtml(activity.attacker)}</span>
            </div>
        `;
        
        if (activity.message) {
            detailsHtml += `<div style="color: #94a3b8; font-size: 0.8em; margin-top: 0.25rem; font-style: italic;">"${escapeHtml(activity.message)}"</div>`;
        }
        
        if (activity.attackMessageId) {
            detailsHtml += `<div style="color: #64748b; font-size: 0.75em; margin-top: 0.25rem;">ID: ${escapeHtml(activity.attackMessageId.substring(0, 16))}...</div>`;
        }
    } else if (activity.attacker && activity.victim) {
        const attackerDisplay = activity.senderName 
            ? `<strong>${escapeHtml(activity.senderName)}</strong> <span style="color: #64748b; font-size: 0.85em;">(${escapeHtml(activity.attacker)})</span>`
            : `<span class="activity-player">${escapeHtml(activity.attacker)}</span>`;
        
        detailsHtml = `
            <div class="activity-action">
                ${attackerDisplay}
                <span style="color: #ef4444; margin: 0 0.5rem;">→</span>
                <span class="activity-victim" title="Victim">${escapeHtml(activity.victim)}</span>
            </div>
        `;
        
        if (activity.message) {
            detailsHtml += `<div style="color: #94a3b8; font-size: 0.8em; margin-top: 0.25rem; font-style: italic;">"${escapeHtml(activity.message)}"</div>`;
        }
        
        if (activity.zombieCount) {
            detailsHtml += `<div style="color: #10b981; font-size: 0.85em; margin-top: 0.25rem;">💀 ${activity.zombieCount} zombies</div>`;
        }
        
        if (activity.strength && activity.strength > 1) {
            detailsHtml += `<div style="color: #f59e0b; font-size: 0.85em; margin-top: 0.25rem;">⚡ Strength: ${activity.strength}</div>`;
        }
        
        if (activity.cost) {
            detailsHtml += `<div style="color: #8b5cf6; font-size: 0.85em; margin-top: 0.25rem;">💰 Cost: ${activity.cost}</div>`;
        }
    } else if (activity.player) {
        const playerDisplay = activity.senderName 
            ? `<strong>${escapeHtml(activity.senderName)}</strong> <span style="color: #64748b; font-size: 0.85em;">(${escapeHtml(activity.player)})</span>`
            : `<span class="activity-player">${escapeHtml(activity.player)}</span>`;
        
        if (activity.prizeMessage) {
            detailsHtml = `
                <div class="activity-action">
                    ${playerDisplay}
                    <div style="color: #10b981; margin-top: 0.25rem;">${escapeHtml(activity.prizeMessage)}</div>
                </div>
            `;
        } else if (activity.candyType) {
            detailsHtml = `
                <div class="activity-action">
                    ${playerDisplay}
                    <div style="color: #f59e0b; margin-top: 0.25rem;">${activity.candyEmoji} ${escapeHtml(activity.candyType)} (${activity.points} pts)</div>
                </div>
            `;
        } else if (activity.direction) {
            detailsHtml = `
                <div class="activity-action">
                    ${playerDisplay}
                    <div style="color: #8b5cf6; margin-top: 0.25rem;">Direction: ${escapeHtml(activity.direction)}</div>
                    ${activity.effect ? `<div style="color: #94a3b8; font-size: 0.8em;">Effect: ${escapeHtml(activity.effect)}</div>` : ''}
                </div>
            `;
        }
    } else if (activity.from) {
        detailsHtml = `
            <div class="activity-action">
                Message from <span class="activity-player">${escapeHtml(activity.from)}</span>
                <span style="color: #64748b; font-size: 0.85em;">(${activity.size} bytes)</span>
            </div>
        `;
    }
    
    // Add rich metadata if available
    if (activity.rssi !== undefined || activity.snr !== undefined || activity.hops !== undefined) {
        const badges = [];
        
        if (activity.rssi) {
            // Reversed: weaker signal (more negative) = better color (green)
            const rssiColor = activity.rssi <= -120 ? '#10b981' : // very weak = green (best)
                             activity.rssi <= -110 ? '#22c55e' : // weak = light green
                             activity.rssi <= -100 ? '#f59e0b' : // fair = orange
                             activity.rssi <= -90 ? '#fb923c' :  // good = light orange
                             '#ef4444';                          // strong = red (worst)
            badges.push(`<span class="badge" style="background: ${rssiColor}22; color: ${rssiColor};">📡 ${activity.rssi} dBm</span>`);
        }
        
        if (activity.snr !== undefined && activity.snr != 0) {
            const snrColor = activity.snr > 0 ? '#10b981' : '#f59e0b';
            badges.push(`<span class="badge" style="background: ${snrColor}22; color: ${snrColor};">SNR ${activity.snr}</span>`);
        }
        
        if (activity.hops !== undefined) {
            badges.push(`<span class="badge" style="background: #8b5cf622; color: #8b5cf6;">🔀 ${activity.hops} hop${activity.hops !== 1 ? 's' : ''}</span>`);
        }
        
        if (activity.viaMqtt) {
            badges.push(`<span class="badge" style="background: #06b6d422; color: #06b6d4;">📶 MQTT</span>`);
        } else if (activity.rssi) {
            badges.push(`<span class="badge" style="background: #10b98122; color: #10b981;">📻 RF</span>`);
        }
        
        if (activity.channel !== undefined) {
            badges.push(`<span class="badge" style="background: #64748b22; color: #64748b;">Ch ${activity.channel}</span>`);
        }
        
        metadataHtml = `<div class="activity-metadata">${badges.join('')}</div>`;
    }
    
    card.innerHTML = `
        <div class="activity-header">
            <div>
                <span class="activity-icon">${icon}</span>
                <span class="activity-type">${activity.displayType || activity.type.replace('_', ' ').toUpperCase()}</span>
            </div>
            <span class="activity-time" data-timestamp="${activity.timestamp}">${time}</span>
        </div>
        <div class="activity-details">
            ${detailsHtml}
            ${metadataHtml}
        </div>
    `;
    
    // Add to top of feed
    container.insertBefore(card, container.firstChild);
    
    // Limit to 50 cards
    while (container.children.length > 50) {
        container.removeChild(container.lastChild);
    }
    
    // Animate in
    setTimeout(() => {
        card.style.opacity = '1';
    }, 10);
}

// Format activity timestamp
function formatActivityTime(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 1000) return 'just now';
    if (diff < 60000) return Math.floor(diff / 1000) + 's ago';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    return new Date(timestamp).toLocaleTimeString();
}

// Update all activity card timestamps
function updateActivityTimestamps() {
    const container = document.getElementById('activityContainer');
    if (!container) return;
    
    const cards = container.querySelectorAll('.activity-card');
    cards.forEach(card => {
        const timeElement = card.querySelector('.activity-time');
        if (timeElement && timeElement.dataset.timestamp) {
            const timestamp = parseInt(timeElement.dataset.timestamp);
            timeElement.textContent = formatActivityTime(timestamp);
        }
    });
}

// Utility: Escape HTML
function escapeHtml(unsafe) {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

// Utility: Format timestamp
function formatTimestamp(timestamp) {
    if (!timestamp) return 'Never';
    
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

console.log('✅ LibP2P Client module loaded');
